import type {
  BuildRoutineTimelineOptions,
  ExpectedAvailability,
  RoutineTimelineView,
  TimelineItemState,
  TimelineRouteItem,
} from '../types/RoutineTimeline.types';
import { formatDurationShort, splitHoursMinutes } from './formatDuration';
import {
  getRanRouteCount,
  getRoutineStep,
  // eslint-disable-next-line max-len
} from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/routineTimeline/utils/routineProgress';
import type {
  TimelineReport,
  // eslint-disable-next-line max-len
} from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/routineTimeline/types/RoutineTimeline.types';
import { CleaningStatusEnum } from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';
import type { Optional } from 'lib/types/Optional';

const FINISHED_ROUTE_STATUSES = [CleaningStatusEnum.Finished, CleaningStatusEnum.Working, CleaningStatusEnum.Unknown];
const SECONDS_PER_MINUTE = 60;

const getExpectedAvailability = (
  durationMs: Optional<number>,
  postChargingDurationMs: Optional<number>
): ExpectedAvailability => {
  if (durationMs == null) {
    return {};
  }

  return {
    cleaningCompletion: splitHoursMinutes(durationMs),
    fullRecharge: postChargingDurationMs == null ? undefined : splitHoursMinutes(durationMs + postChargingDurationMs),
  };
};

const getFinishedItem = (
  base: Pick<TimelineRouteItem, 'id' | 'name'>,
  report: Optional<TimelineReport>,
  isReportUnavailable: boolean
): TimelineRouteItem => {
  // No report yet: it may still turn out cancelled (e.g. skipped), so don't claim completed.
  if (!report && !isReportUnavailable) {
    return { ...base, state: 'finishing' };
  }

  // The report never came: show it as done, without numbers.
  if (!report) {
    return { ...base, state: 'completed', isReportUnavailable };
  }

  const state: TimelineItemState = FINISHED_ROUTE_STATUSES.includes(report.status) ? 'completed' : 'cancelled';

  return {
    ...base,
    state,
    reportId: report.exportId ?? undefined,
    metrics: {
      coveragePercent: Math.round(report.pathCoverage * 100),
      durationMinutes: Math.round(report.cleanedTime / SECONDS_PER_MINUTE),
    },
  };
};

/**
 * The timeline rows of a routine run. Row `i` is the routine's `i`th route; rows before the task's `step` are
 * done, the row at `step` is being cleaned and the rest are upcoming. A run stopped early is read from its reports:
 * routes after the last one that ran are not cleaned. Null while the routine is not loaded.
 */
export const buildRoutineTimeline = ({
  timeline,
  task,
  currentRoute,
  scheduleName,
  isRobotCleaning,
  isStoppedEarly = false,
}: BuildRoutineTimelineOptions): RoutineTimelineView | null => {
  const { routine, routes, reports, unavailableRouteIndexes, stoppedStep } = timeline;

  if (!routine) {
    return null;
  }

  const totalRoutes = routine.routeIds.length;
  const expectedAvailability = getExpectedAvailability(routine.durationMs, routine.postChargingDurationMs);

  if (isStoppedEarly) {
    const ranRouteCount = Math.min(getRanRouteCount({ reports, stoppedStep }), totalRoutes);

    return {
      scheduleName,
      completedRoutes: ranRouteCount,
      totalRoutes,
      timeline: routine.routeIds.map((routeId, index): TimelineRouteItem => {
        const position = index + 1;
        const base = { id: `${position}-${routeId}`, name: routes[routeId]?.name || `#${routeId}` };
        const report = reports.find(({ routeIndex }) => routeIndex === position);

        if (!report && position === stoppedStep) {
          return { ...base, state: 'stopped' };
        }

        if (!report && position > ranRouteCount) {
          return { ...base, state: 'notCleaned' };
        }

        return getFinishedItem(base, report, unavailableRouteIndexes.includes(position));
      }),
      expectedAvailability,
    };
  }

  // `step` ends one above the route count on a finished task.
  const lastRouteName = routes[routine.routeIds[totalRoutes - 1]]?.name;
  const step = getRoutineStep({
    step: task?.step,
    taskStatus: task?.taskStatus,
    totalRoutes,
    taskName: task?.name,
    lastRouteName,
    isRobotCleaning,
  });

  const items = routine.routeIds.map((routeId, index): TimelineRouteItem => {
    const position = index + 1;
    const route = routes[routeId];
    const base = { id: `${position}-${routeId}`, name: route?.name || `#${routeId}` };

    if (position < step) {
      return getFinishedItem(
        base,
        reports.find(report => report.routeIndex === position),
        unavailableRouteIndexes.includes(position)
      );
    }

    if (position === step) {
      return {
        ...base,
        state: 'cleaning',
        minutesLeft: currentRoute.minutesLeft,
        progressPercent: currentRoute.progressPercent,
        batteryPercent: currentRoute.batteryPercent,
        waterLiters: currentRoute.waterLiters,
        waterPercent: currentRoute.waterPercent,
      };
    }

    return { ...base, state: 'upcoming', estimatedDuration: formatDurationShort(route?.durationMs) };
  });

  return {
    scheduleName,
    completedRoutes: Math.min(step, totalRoutes),
    totalRoutes,
    timeline: items,
    expectedAvailability,
  };
};
